/* Clérolis - custom behaviors. No external libraries; cooperates with Dawn's cart. */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
   * <clerolis-announcement> - gentle cross-fade rotation of messages.
   * Attributes: data-rotate ("true"), data-speed (seconds).
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-announcement')) {
    customElements.define(
      'clerolis-announcement',
      class ClerolisAnnouncement extends HTMLElement {
        connectedCallback() {
          // Publish the announcement bar's full height so the hero can size itself to
          // "full viewport minus the announcement bar". Recompute on resize + after fonts.
          this.publishHeight();
          this.onResize = this.publishHeight.bind(this);
          window.addEventListener('resize', this.onResize);
          if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.onResize);

          this.messages = Array.from(this.querySelectorAll('.clerolis-announcement__message'));
          if (this.dataset.rotate !== 'true' || this.messages.length < 2) return;
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          this.index = 0;
          const speed = (parseFloat(this.dataset.speed) || 5) * 1000;
          this.timer = setInterval(this.advance.bind(this), speed);
        }

        publishHeight() {
          const bar = this.closest('.shopify-section') || this.closest('.clerolis-announcement') || this;
          document.documentElement.style.setProperty('--clerolis-announcement-h', (bar.offsetHeight || 0) + 'px');
        }

        disconnectedCallback() {
          if (this.timer) clearInterval(this.timer);
          if (this.onResize) window.removeEventListener('resize', this.onResize);
        }

        advance() {
          this.messages[this.index].classList.remove('is-active');
          this.index = (this.index + 1) % this.messages.length;
          this.messages[this.index].classList.add('is-active');
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * <clerolis-sticky-atc> - reveals a fixed add-to-cart bar once the main
   * buy box scrolls out of view, then proxies the click to the real button.
   * Attributes: data-buybox (selector of the element to observe),
   *             data-atc (selector of the real add-to-cart button).
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-sticky-atc')) {
    customElements.define(
      'clerolis-sticky-atc',
      class ClerolisStickyAtc extends HTMLElement {
        connectedCallback() {
          const target = document.querySelector(this.dataset.buybox || '');
          this.proxy = this.querySelector('[data-atc-proxy]');
          if (this.proxy) {
            this.proxy.addEventListener('click', () => {
              const realBtn = document.querySelector(this.dataset.atc || '');
              if (realBtn) realBtn.click();
            });
          }
          // Square caret button: scroll the page up to the real add-to-cart button.
          this.scrollBtn = this.querySelector('[data-atc-scroll]');
          if (this.scrollBtn) {
            this.scrollBtn.addEventListener('click', () => {
              const realBtn = document.querySelector(this.dataset.atc || '');
              const dest = realBtn || document.querySelector(this.dataset.buybox || '');
              if (!dest) return;
              dest.scrollIntoView({ behavior: 'smooth', block: 'center' });
              if (realBtn && typeof realBtn.focus === 'function') {
                setTimeout(() => {
                  try {
                    realBtn.focus({ preventScroll: true });
                  } catch (e) {
                    realBtn.focus();
                  }
                }, 450);
              }
            });
          }
          // Mirror the real add-to-cart button's availability: when Dawn
          // disables it (sold out / unavailable variant), the sticky bar
          // disables its button and shows the sold-out label too.
          const realBtn = document.querySelector(this.dataset.atc || '');
          const soldout = this.querySelector('[data-atc-soldout]');
          if (realBtn) {
            const syncAvailability = () => {
              const unavailable = realBtn.disabled;
              if (this.scrollBtn) this.scrollBtn.disabled = unavailable;
              if (soldout) soldout.hidden = !unavailable;
            };
            syncAvailability();
            if ('MutationObserver' in window) {
              new MutationObserver(syncAvailability).observe(realBtn, {
                attributes: true,
                attributeFilter: ['disabled'],
              });
            }
          }

          if (!target || !('IntersectionObserver' in window)) return;
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                // Visible only after the buy box has scrolled above the viewport.
                const below = entry.boundingClientRect.top > 0;
                this.classList.toggle('is-visible', !entry.isIntersecting && !below);
              });
            },
            { rootMargin: '0px 0px -80% 0px', threshold: 0 }
          );
          this.observer.observe(target);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * <clerolis-add-duo> - one-click "Ajouter le duo": adds two variants to
   * the cart in a single request and refreshes Dawn's cart drawer.
   * Attributes: data-variants (comma-separated variant IDs).
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-add-duo')) {
    customElements.define(
      'clerolis-add-duo',
      class ClerolisAddDuo extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.button.addEventListener('click', this.addDuo.bind(this));
        }

        addDuo() {
          const ids = (this.dataset.variants || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
          if (!ids.length) return;

          this.button.setAttribute('aria-busy', 'true');
          this.button.disabled = true;

          const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
          const cartDrawer = document.querySelector('cart-drawer');
          const body = { items: items };

          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
            body.sections_url = window.location.pathname;
          }

          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          })
            .then((response) => response.json())
            .then((parsed) => {
              if (parsed.status) {
                this.showError(parsed.description || parsed.message);
                return;
              }
              if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                cartDrawer.renderContents(parsed);
                if (typeof cartDrawer.open === 'function') cartDrawer.open();
              } else {
                window.location = window.Shopify.routes.root + 'cart';
              }
            })
            .catch(() => {
              window.location = window.Shopify.routes.root + 'cart';
            })
            .finally(() => {
              this.button.removeAttribute('aria-busy');
              this.button.disabled = false;
            });
        }

        showError(message) {
          const el = this.querySelector('[data-duo-error]');
          if (el && message) {
            el.textContent = message;
            el.hidden = false;
          }
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * <clerolis-volume-tiers> - "buy more, save more" offer selector.
   * Each offer is a radio whose value is the quantity; selecting one sets the
   * product form's hidden quantity input and highlights the active card.
   * The displayed discount is set in the editor; pair it with a Shopify
   * automatic discount (same quantity) to enforce it at checkout.
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-volume-tiers')) {
    customElements.define(
      'clerolis-volume-tiers',
      class ClerolisVolumeTiers extends HTMLElement {
        connectedCallback() {
          this.qtyInput = this.querySelector('[data-tier-quantity]');
          this.priceTarget = this.dataset.priceTarget ? document.querySelector(this.dataset.priceTarget) : null;
          this.radios = Array.from(this.querySelectorAll('.clerolis-tier__radio'));
          if (!this.radios.length) return;
          this.radios.forEach((radio) => radio.addEventListener('change', () => this.select(radio)));
          const checked = this.radios.find((radio) => radio.checked) || this.radios[0];
          this.select(checked);
        }

        select(radio) {
          if (!radio) return;
          this.radios.forEach((r) => {
            const card = r.closest('.clerolis-tier__inner');
            if (card) card.classList.toggle('is-active', r === radio);
          });
          if (!radio.checked) radio.checked = true;
          if (this.qtyInput) this.qtyInput.value = radio.value;
          this.updatePrice(radio);
        }

        updatePrice(radio) {
          if (!this.priceTarget) return;
          const now = radio.dataset.now;
          if (!now) return;
          const was = radio.dataset.was;
          let html = '<span class="clerolis-product__price-now">' + now + '</span>';
          if (was) html += ' <s class="clerolis-product__price-was">' + was + '</s>';
          this.priceTarget.innerHTML = html;
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * <clerolis-carousel> - lightweight, dependency-free slider with dots.
   * Carousel mode (horizontal scroll-snap + dots) is on for mobile always,
   * and on desktop only when the slide count exceeds data-desktop-min;
   * otherwise the track stays a static grid. Dots reflect/scroll position.
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-carousel')) {
    customElements.define(
      'clerolis-carousel',
      class ClerolisCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.clerolis-carousel__track');
          this.dots = Array.from(this.querySelectorAll('.clerolis-carousel__dot'));
          this.slides = this.track ? Array.from(this.track.children) : [];
          if (!this.track || !this.slides.length) return;
          this.desktopMin = parseInt(this.dataset.desktopMin, 10) || 3;
          this.mq = window.matchMedia('(min-width: 750px)');
          this.onResize = this.update.bind(this);
          window.addEventListener('resize', this.onResize);
          this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
          this.track.addEventListener(
            'scroll',
            () => {
              if (this.raf) return;
              this.raf = requestAnimationFrame(() => {
                this.raf = null;
                this.syncActive();
              });
            },
            { passive: true }
          );
          this.update();
        }

        update() {
          const carousel = !this.mq.matches || this.slides.length > this.desktopMin;
          this.classList.toggle('is-carousel', carousel);
          this.syncActive();
        }

        goTo(index) {
          const slide = this.slides[index];
          if (!slide) return;
          const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
          this.track.scrollTo({ left: left, behavior: 'smooth' });
        }

        syncActive() {
          if (!this.dots.length) return;
          const trackLeft = this.track.getBoundingClientRect().left;
          let best = 0;
          let bestDist = Infinity;
          this.slides.forEach((slide, i) => {
            const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });
          this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === best));
        }

        disconnectedCallback() {
          if (this.onResize) window.removeEventListener('resize', this.onResize);
        }
      }
    );
  }

  const prefersReduced = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ----------------------------------------------------------------------
   * <clerolis-expandable> - clamps long content (e.g. the product
   * description) and reveals the rest with an animated "Voir plus / moins".
   * Attributes: data-collapsed (collapsed height in px, default 200).
   * -------------------------------------------------------------------- */
  if (!customElements.get('clerolis-expandable')) {
    customElements.define(
      'clerolis-expandable',
      class ClerolisExpandable extends HTMLElement {
        connectedCallback() {
          this.content = this.querySelector('.clerolis-expandable__content');
          this.toggle = this.querySelector('.clerolis-expandable__toggle');
          if (!this.content || !this.toggle) return;
          this.collapsedH = parseInt(this.dataset.collapsed, 10) || 200;
          this.expanded = false;
          // Measure after layout/fonts so we clamp against the real height.
          requestAnimationFrame(() => this.setup());
          if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.setup());
        }

        setup() {
          if (this.ready) return;
          const full = this.content.scrollHeight;
          // Nothing to clamp - leave it fully open, no toggle.
          if (full <= this.collapsedH + 24) {
            this.toggle.hidden = true;
            return;
          }
          this.ready = true;
          this.classList.add('is-collapsible');
          this.content.style.overflow = 'hidden';
          this.content.style.maxHeight = this.collapsedH + 'px';
          this.toggle.hidden = false;
          this.toggle.addEventListener('click', this.onToggle.bind(this));
        }

        onToggle() {
          if (this.expanded) this.collapse();
          else this.expand();
        }

        expand() {
          this.expanded = true;
          this.toggle.setAttribute('aria-expanded', 'true');
          this.classList.add('is-expanded');
          if (prefersReduced()) {
            this.content.style.maxHeight = 'none';
            return;
          }
          const target = this.content.scrollHeight;
          this.content.style.transition = 'max-height 0.35s ease';
          this.content.style.maxHeight = target + 'px';
          const done = (e) => {
            if (e.propertyName !== 'max-height') return;
            this.content.style.transition = '';
            this.content.style.maxHeight = 'none';
            this.content.removeEventListener('transitionend', done);
          };
          this.content.addEventListener('transitionend', done);
        }

        collapse() {
          this.expanded = false;
          this.toggle.setAttribute('aria-expanded', 'false');
          this.classList.remove('is-expanded');
          if (prefersReduced()) {
            this.content.style.maxHeight = this.collapsedH + 'px';
            return;
          }
          // From its current full height down to the collapsed height.
          this.content.style.maxHeight = this.content.scrollHeight + 'px';
          requestAnimationFrame(() => {
            this.content.style.transition = 'max-height 0.35s ease';
            this.content.style.maxHeight = this.collapsedH + 'px';
          });
          const done = (e) => {
            if (e.propertyName !== 'max-height') return;
            this.content.style.transition = '';
            this.content.removeEventListener('transitionend', done);
          };
          this.content.addEventListener('transitionend', done);
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
   * Animated accordions - native <details.clerolis-accordion__item> don't
   * animate height, so we drive the open/close with a height transition.
   * Used by the PDP collapsible rows and the FAQ section.
   * -------------------------------------------------------------------- */
  function enhanceAccordion(details) {
    if (details.dataset.clerolisEnhanced || prefersReduced()) return;
    const summary = details.querySelector('summary');
    const content = details.querySelector('.clerolis-accordion__content');
    if (!summary || !content) return;
    details.dataset.clerolisEnhanced = 'true';
    // Drop the native exclusive-group name: it would instantly close siblings
    // when we set details.open, fighting our animation. Exclusivity is handled
    // in JS below instead (the name is kept only for the no-JS / reduced-motion
    // fallback, which relies on the browser's native grouping).
    details.removeAttribute('name');
    content.style.overflow = 'hidden';
    content.style.height = details.open ? 'auto' : '0px';
    details.classList.toggle('is-open', details.open);

    const EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';
    const DURATION = 270;
    let anim = null;
    // 'closed' | 'opening' | 'open' | 'closing'
    let state = details.open ? 'open' : 'closed';

    // Web Animations API drives the height: smoother and free of the
    // rAF + transitionend stutter the previous CSS-transition approach had.
    function run(opening) {
      if (anim) anim.cancel();
      const from = content.getBoundingClientRect().height; // 0 when fully closed
      if (opening) details.open = true; // content must be rendered to measure it
      const to = opening ? content.scrollHeight : 0;
      content.style.height = from + 'px'; // pin start to avoid a first-frame flash
      details.classList.toggle('is-open', opening);
      state = opening ? 'opening' : 'closing';

      anim = content.animate(
        {
          height: [from + 'px', to + 'px'],
          opacity: opening ? [0.4, 1] : [1, 0.4],
        },
        { duration: DURATION, easing: EASE }
      );
      anim.onfinish = () => {
        anim = null;
        if (opening) {
          content.style.height = 'auto';
          state = 'open';
        } else {
          details.open = false;
          content.style.height = '0px';
          state = 'closed';
        }
      };
    }

    // Let sibling accordions collapse this one (exclusive open behaviour).
    details.clerolisClose = () => {
      if (state === 'open' || state === 'opening') run(false);
    };

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      const opening = state === 'closed' || state === 'closing';
      if (opening && details.parentElement) {
        // Only one open at a time within the same group: close open siblings.
        details.parentElement
          .querySelectorAll(':scope > details.clerolis-accordion__item')
          .forEach((sibling) => {
            if (sibling !== details && typeof sibling.clerolisClose === 'function') {
              sibling.clerolisClose();
            }
          });
      }
      run(opening);
    });
  }

  function initAccordions(root) {
    (root || document).querySelectorAll('details.clerolis-accordion__item').forEach(enhanceAccordion);
  }

  if (document.readyState !== 'loading') initAccordions();
  else document.addEventListener('DOMContentLoaded', () => initAccordions());
  // Re-enhance sections re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', (event) => initAccordions(event.target));
})();
